import { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { RiDeleteBinLine } from 'react-icons/ri';

type Props = {
  id: number;
  onDelete: (id: number) => Promise<void>;
};

const DeleteMemberModal = ({ id, onDelete }: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const closeModal = () => {
    if (!isDeleting) {
      setModalOpen(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(id);
      closeModal();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Error deleting team member:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (keyCode === 27 && !isDeleting) closeModal();
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [isDeleting]);

  return (
    <>
      <button onClick={() => setModalOpen(!modalOpen)}>
        <RiDeleteBinLine color='red' />
      </button>

      {modalOpen && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
          onClick={!isDeleting ? closeModal : undefined}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
          >
            <button
              className='text-gray-600 hover:text-gray-900 absolute right-4 top-4 disabled:cursor-not-allowed disabled:opacity-50'
              onClick={closeModal}
              disabled={isDeleting}
            >
              <RxCross2 />
            </button>
            <div className='p-8'>
              <div className='mb-4'>
                <h2 className='mb-[30px] text-[22px] font-bold'>
                  Delete Team Member?
                </h2>
              </div>

              <p className='mb-[30px]'>
                Are you sure you want to delete this team member?
              </p>

              <div className='flex justify-start space-x-4'>
                <button
                  onClick={closeModal}
                  disabled={isDeleting}
                  className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                >
                  No, Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className='rounded-full bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:bg-yellow disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteMemberModal;
